import React, { useState } from 'react';
import './Contact.css';
import { useLanguage } from '../context/LanguageContext';
import Toast from '../components/Toast';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState({ loading: false, error: null, success: null });
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'error') => {
    setToast({ message, type });
  };

  const validateForm = () => {
    const { name, email, phone } = formData;

    // Name validation
    if (name.trim().length < 3) {
      return t('validation.nameError');
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return t('validation.emailError');
    }

    // Phone validation (10 digits and starts with 6-9)
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      return t('validation.phoneError');
    }
    if (!/^[6-9]/.test(phoneDigits)) {
      return t('validation.phoneStartError');
    }

    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Strict numeric only for phone
    if (name === 'phone') {
      const numericValue = value.replace(/\D/g, '').substring(0, 10);
      setFormData({ ...formData, [name]: numericValue });
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      showToast(validationError, 'error');
      return;
    }

    setStatus({ loading: true, error: null, success: null });

    try {
      const response = await fetch('https://mhaskobaadmin.tipic.in/api/contact-usstore', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'काहीतरी चूक झाली');
      }

      // Success
      showToast(result.message || 'संदेश यशस्वीपणे पाठवला गेला!', 'success');

      // Reset form
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setStatus({ loading: false, success: true, error: null });

    } catch (err) {
      showToast(err.message || 'सर्व्हरशी कनेक्ट होऊ शकले नाही', 'error');
      setStatus({ loading: false, success: null, error: true });
    }
  };

  return (
    <div className="contact-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <section className="contact-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <span className="hero-pill">{t('nav.contact')}</span>
          <h1>{t('contact.heroTitle')}</h1>
          <div className="header-divider">
            <span className="dot"></span>
            <span className="line"></span>
            <span className="dot"></span>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="contact-content">
          <div className="contact-info-section">
            <div className="info-box">
              <h3>{t('contact.addressTitle')}</h3>
              <p>
                {t('contact.addressText').split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}<br />
                  </React.Fragment>
                ))}
              </p>
            </div>

            <div className="info-box">
              <h3>{t('contact.phoneTitle')}</h3>
              <p>
                {t('contact.phoneText').split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}<br />
                  </React.Fragment>
                ))}
              </p>
            </div>

            <div className="info-box">
              <h3>{t('contact.emailTitle')}</h3>
              <p>
                {t('contact.emailText').split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}<br />
                  </React.Fragment>
                ))}
              </p>
            </div>

            <div className="info-box">
              <h3>{t('contact.timingTitle')}</h3>
              <p>
                {t('contact.timingText').split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}<br />
                  </React.Fragment>
                ))}
                <br />
                {t('contact.officeTimingTitle')}:<br />
                {t('contact.officeTimingText').split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}<br />
                  </React.Fragment>
                ))}
              </p>
            </div>
          </div>
        </div>

        <div className="contact-form-section">
          <h2>{t('contact.formTitle')}</h2>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>{t('contact.fullName')}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.fullNamePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('contact.emailAddress')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('contact.phoneNumber')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.phonePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label>{t('contact.subject')}</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.subjectPlaceholder')}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('contact.message')}</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="6"
                placeholder={t('contact.messagePlaceholder')}
              ></textarea>
            </div>

            <button type="submit" className="submit-btn" disabled={status.loading}>
              {status.loading ? 'Sending...' : t('contact.sendMessage')}
            </button>
          </form>
        </div>

        <section className="map-section">
          <h2 className="section-title">{t('contact.findUs')}</h2>
          <div className="divider"></div>
          <div className="map-container">
            <iframe
              title={t('contact.mapTitle')}
              src="https://www.google.com/maps?q=Shri%20Kalbhairavnath%20Mhaskoba%20Maharaj%20Temple%2C%20Kodit%2C%20Purandar%2C%20Pune%2C%20Maharashtra%20412108&output=embed"
              width="100%"
              height="400"
              style={{ border: 0, borderRadius: '15px' }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Contact;









